import path from 'path';

import colors from 'tailwindcss/colors';
import plugin from 'tailwindcss/plugin';

import { base as baseColors, semantic as semanticColors } from './colors';
import * as Components from './css/components';
import * as DynamicUtility from './css/dynamic-utilities';
import * as StaticUtility from './css/static-utilities';

import type { Config } from 'tailwindcss';

const webCompositionsPath = path.resolve(
  __dirname,
  '../../web-compositions/src/components',
);

const coreComponentsPath = path.resolve(
  __dirname,
  '../../../node_modules/@tui-cruises/mein-schiff-web-react-component-library/src/components',
);

type TuicTailwindConfig = Config & {
  content: string[];
};

const config: TuicTailwindConfig = {
  content: [
    path.relative('./', webCompositionsPath) + '/**/*.{js,ts,jsx,tsx,mdx}',
    path.relative('./', coreComponentsPath) + '/**/*.{js,ts,jsx,tsx,mdx}',
  ],
  theme: {
    fontFamily: {
      mono: 'monospace',
      sans: ['var(--font-source-sans)'],
    },
    container: {
      center: true,
      padding: '2rem',
      screens: {
        '2xl': '1392px',
      },
    },
    colors: {
      ...baseColors,
      ...semanticColors,
      inherit: colors.inherit,
      current: colors.current,
      transparent: colors.transparent,
    },
    borderRadius: {
      xs: '.125rem',
      sm: '.25rem',
      md: '.5rem',
      lg: '1rem',
      full: '9999px',
      none: '0rem',
    },
    boxShadow: {
      none: 'none',
      100: '0px 2px 4px 0px rgba(38, 62, 106, 0.10)',
      200: '0px 8px 16px 0px rgba(0, 0, 0, 0.06)',
      sm: '0px 2px 8px -2px rgba(38, 62, 106, 0.10)',
      md: '0px 2px 16px 0px rgba(38, 62, 106, 0.15)',
      xl: '0 8px 10px -6px rgba(38, 62, 106, 0.1), 0 20px 25px -5px rgba(38, 62, 106, 0.1)',
      'focus-state': '0 0 0 2px white, 0 0 0 4px #1A65A7, 0 0 0 6px #A9D0F5',
    },
    spacing: {
      px: '1px',
      0: '0px',
      0.5: '0.125rem',
      1: '0.25rem',
      1.5: '0.375rem',
      2: '0.5rem',
      2.5: '0.625rem',
      3: '0.75rem',
      4: '1rem',
      5: '1.25rem',
      6: '1.5rem',
      7: '1.75rem',
      8: '2rem',
      9: '2.25rem',
      10: '2.5rem',
      11: '2.75rem',
      12: '3rem',
      14: '3.5rem',
      16: '4rem',
      18: '4.5rem',
      20: '5rem',
      25: '6.25rem',
      30: '7.5rem',
    },
    fontSize: {
      xs: ['0.75rem', { lineHeight: '1.6' }],
      sm: ['0.875rem', { lineHeight: '1.6' }],
      base: ['1rem', { lineHeight: '1.6' }],
      '2xl': ['1.5rem', { lineHeight: '1.2', fontWeight: '600' }], // @deprecated
      '400': ['1.125rem', { lineHeight: '1.6' }],
      '500': ['1.25rem', { lineHeight: '1.6' }],
      '700': ['1.75rem', { lineHeight: '1.3' }],
      '800': ['2rem', { lineHeight: '1.2' }],
      '900': ['2.5rem', { lineHeight: '1.2' }],
      '1100': ['3.75rem', { lineHeight: '1.2' }],
    },
    fontWeight: {
      normal: '400',
      semibold: '600',
    },
    extend: {
      screens: {
        xs: '375px',
      },
      aspectRatio: {
        '1/1': '1/1',

        '3/1': '3/1',

        '5/4': '5/4',
        '4/5': '4/5',

        '16/9': '16/9',
        '9/16': '9/16',

        '4/3': '4/3',
        '3/4': '3/4',

        '3/2': '3/2',
        '2/3': '2/3',

        '21/9': '21/9',
        '9/21': '9/21',

        '32/9': '32/9',
        '9/32': '9/32',
      },
      keyframes: {
        'accordion-down': {
          from: { height: '0' },
          to: { height: 'var(--radix-accordion-content-height)' },
        },
        'accordion-up': {
          from: { height: 'var(--radix-accordion-content-height)' },
          to: { height: '0' },
        },
        'collapsible-down': {
          from: { height: '0' },
          to: { height: 'var(--radix-collapsible-content-height)' },
        },
        'collapsible-up': {
          from: { height: 'var(--radix-collapsible-content-height)' },
          to: { height: '0' },
        },
        'favorite-pulse': {
          '0%': { transform: 'scale(66%)' },
          '50%': { transform: 'scale(133%)' },
          '100%': { transform: 'scale(100%)' },
        },
        'compass-roll': {
          '0%': { transform: 'translateY(0%)' },
          '100%': { transform: 'translateY(calc(-100% + 3.75rem))' },
        },
        'scroll-indicator': {
          '0%': { transform: 'translateY(0%)' },
          '100%': { transform: 'translateY(-0.5rem)' },
        },
      },
      animation: {
        'accordion-down': 'accordion-down 0.2s ease-out',
        'accordion-up': 'accordion-up 0.2s ease-out',
        'collapsible-down': 'collapsible-down 0.2s ease-out',
        'collapsible-up': 'collapsible-up 0.2s ease-out',
        'favorite-pulse': 'favorite-pulse 0.6s ease-in-out',
        'compass-roll':
          'compass-roll var(--animation-duration) ease forwards 1.5s',
        'scroll-indicator':
          'scroll-indicator 1s cubic-bezier(.37,.81,.3,1.01) 1s infinite alternate',
      },
      transitionDuration: {
        DEFAULT: '150ms',
        0: '0s',
        75: '75ms',
        100: '100ms',
        150: '150ms',
        200: '200ms',
        300: '300ms',
        400: '400ms',
        500: '500ms',
        700: '700ms',
        1000: '1000ms',
      },
      rotate: {
        '135': '135deg',
        '225': '225deg',
      },
      width: {
        '1/7': '14.2857%',
      },
      zIndex: {
        header: '900',
      },
    },
  },
  future: {
    hoverOnlyWhenSupported: true,
  },
  plugins: [
    require('tailwindcss-animate'),
    require('tailwind-gradient-mask-image'),
    require('@tailwindcss/container-queries'),
    plugin(api => {
      api.addUtilities(Object.values(StaticUtility));

      Object.values(DynamicUtility).forEach(utility => utility(api));

      api.addComponents(
        Object.values(Components).map(component => {
          return typeof component === 'function' ? component(api) : component;
        }),
      );
    }),

    // firefox only modifier
    plugin(({ addVariant }) => {
      addVariant('firefox', ':-moz-any(&)');
    }),
  ],
};

export default config;
